<script setup lang="ts">
import { phoneValidator } from "~/services/helpers";
import { computed } from "vue";
import type {
  ConversionsInterface,
  ExistsProductOptionsInterface,
  ProductInterface,
} from "~~/server/types/Products";
import "vue3-carousel/carousel.css";
import { Carousel, Navigation, Slide } from "vue3-carousel";
import { useProduct } from "~/composables/useProduct";
import { usePrice } from "~/composables/usePrice";
import { useCart } from "~/composables/useCart";
import { useFavorites } from "~/composables/useFavorites";
import { tracking } from "~/services/tracking.client";
import { storeToRefs } from "pinia";
import { useOrder } from "~/composables/useOrder";
import type { userInterface } from "~~/server/types/userInterface";
import type { FormInstance, FormRules } from "element-plus";
import { useI18n } from "vue-i18n";
import type { PageInterface } from "~~/server/types/page";
import type { OptionDataInterface } from '#server/types/CartInterface';
import { useGlobalDialog } from '~/composables/useGlobalDialog';
import ProductPreorderTerms from '~/components/products/ProductPreorderTerms.vue';
import ProductSubscriptionTerms from '~/components/products/ProductSubscriptionTerms.vue';
import type {checkoutInterface} from "#server/types/OrderInterface";
const { openDialog } = useGlobalDialog()
const UploadFileToOptionComponent = defineAsyncComponent(
  () => import('~/components/dialogs/UploadFileToOptionComponent.vue'),
);
const BookingCalendar = defineAsyncComponent(
  () => import('~/components/products/BookingCalendar.vue'),
);
const ProductListSimple = defineAsyncComponent(
  () => import("~/components/products/ProductListSimple.vue"),
);
const RateComponent = defineAsyncComponent(
  () => import("~/components/products/RateComponent.vue"),
);

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);
const {
  getMaxAvailableCountPcs,
  getBasePrice,
  getOptionExtras,
  related,
  colors,
  pages,
} = useProduct();
const {
  in_favorites,
  addToFavorites,
  removeFromFavorites,
  adding: favLoading,
} = useFavorites();

const { t } = useI18n();

const route = useRoute();
const { locale } = useI18n();
const productsStore = useProductStore();
const cartStore = useCartStore();
const userStore = useAuthStore();

const { format, oldPrice } = usePrice();
const { addToCart, adding } = useCart();

const routeParam = (param: unknown) =>
  Array.isArray(param) ? String(param[0] ?? "") : String(param ?? "");

const product_slug = computed(
  () =>
    `${routeParam(route.params.category_slug)}/${routeParam(
      route.params.product_slug,
    )}`,
);
const key_product = computed(
  () => "product:" + product_slug.value + ":" + locale.value,
);
const { data, error } = await useAsyncData<ProductInterface>(
  key_product.value,
  () => productsStore.loadSingleProduct(product_slug.value),
);

if (error.value) showError(error.value);
const product = computed<ProductInterface | undefined>(() => data.value);
const subscriptionMonths = ref<1 | 3 | 6 | 12 | null>(null);
const subscriptionCheckoutAvailable = computed(() => settings.value?.subscriptions?.checkout_available === true);
const offersSubscriptions = computed(() => product.value?.subscription?.eligible && Object.keys(product.value.subscription.terms).length > 0 &&
  !product.value.is_preorder && !product.value.is_booking);

const bookingStart = ref<string | null>(null);
const bookingCalendar = ref<InstanceType<typeof BookingCalendar> | null>(null);
const currentSlide = ref(0);
const loadedSlides = ref(new Set([0]));
watch(currentSlide, index => loadedSlides.value.add(index), { flush: 'sync' });
const is_mobile = ref(false);

type ProductGalleryImage = ProductInterface["gallery"][number];
type ProductOptionValue = ExistsProductOptionsInterface["values"][number];
type SelectedOptions = Record<number, number | number[]>;

const designs = {
  radio_required: 1,
  select: 2,
  checkbox: 3,
  file: 4,
  text: 5,
} as const;
const minOrderCount = 1;

const form = ref({
  code: "PCS" as ConversionsInterface["code"],
  count: 1,
  options: {} as SelectedOptions,
  options_data:{} as OptionDataInterface
});

const slideTo = (nextSlide: number) => (currentSlide.value = nextSlide);

const galleryConfig = {
  itemsToShow: 1,
  wrapAround: true,
  slideEffect: "fade" as const,
  mouseDrag: true,
  touchDrag: true,
  height: "auto" as const,
};

const thumbnailsConfig = {
  height: 80,
  itemsToShow: 6,
  wrapAround: true,
  touchDrag: false,
  gap: 10,
};

const images = computed<ProductGalleryImage[]>(() => {
  const img: ProductGalleryImage[] = [];
  const seen = new Set<string>();
  const pushImage = (image?: Partial<ProductGalleryImage> | null) => {
    const src = image?.photo || image?.thumb || image?.icon;
    if (!src || seen.has(src)) return;

    seen.add(src);
    img.push({
      icon: image.icon || src,
      thumb: image.thumb || src,
      photo: image.photo || image.thumb || src,
    });
  };

  if (product.value) {
    pushImage({
      icon: product.value.icon,
      thumb: product.value.thumb || product.value.icon,
      photo: product.value.photo || product.value.thumb || product.value.icon,
    });
  }

  if (Array.isArray(product.value?.gallery)) {
    product.value.gallery.forEach(pushImage);
  }

  return img;
});

const galleryFallbackImage = computed(
  () =>
    images.value[0]?.photo ||
    images.value[0]?.thumb ||
    product.value?.photo ||
    product.value?.thumb ||
    product.value?.icon ||
    "",
);

const options = computed<ExistsProductOptionsInterface[]>(() =>
  [...(product.value?.options ?? [])].sort((a, b) => a.design - b.design),
);

const selected_conversion = computed<ConversionsInterface | null>(() => {
  const conversions = (product.value?.conversions ?? []).filter(c => !product.value?.is_booking || c.conversion === 1);
  return (
    conversions.find((c) => c.code === form.value.code) ||
    conversions[0] ||
    null
  );
});

const selectedConversionAmount = computed(
  () => selected_conversion.value?.conversion || 1,
);

const isOptionValueAvailable = (
  value: ProductOptionValue,
  pcs = real_pcs_selected.value,
) => value.count === null || pcs <= value.count;

const availableOptionValues = (option: ExistsProductOptionsInterface) =>
  option.values.filter((value) => isOptionValueAvailable(value));

const getPreferredOptionValue = (
  option: ExistsProductOptionsInterface,
  pcs = real_pcs_selected.value,
) => {
  const fromProduct = option.values.filter((v) => v.part_from_product);
  const preferredValues = fromProduct.length ? fromProduct : option.values;

  return (
    preferredValues.find((value) => isOptionValueAvailable(value, pcs)) ||
    preferredValues[0] ||
    null
  );
};

const buildDefaultOptions = (
  opts: ExistsProductOptionsInterface[] = [],
  pcs = real_pcs_selected.value,
) => {
  const next: SelectedOptions = {};
  for (const option of opts) {
    const fromProduct = option.values.filter((v) => v.part_from_product);

    if (option.design === designs.text) continue;

    if (option.design === designs.checkbox) {
      const selected = fromProduct
        .filter((value) => isOptionValueAvailable(value, pcs))
        .map((value) => value.id);
      if (selected.length) next[option.id] = selected;
      continue;
    }

    const shouldPreselect =
      option.design === designs.radio_required ||
      option.design === designs.select ||
      fromProduct.length > 0;

    if (shouldPreselect) {
      const value = getPreferredOptionValue(option, pcs);
      if (value) next[option.id] = value.id;
    }
  }

  return next;
};

const areSelectedOptionsEqual = (a: SelectedOptions, b: SelectedOptions) =>
  JSON.stringify(a) === JSON.stringify(b);

const syncSelectedOptions = () => {
  const next: SelectedOptions = {};

  for (const option of options.value) {
    const selected = form.value.options[option.id];
    const availableValues = availableOptionValues(option);
    const availableIds = new Set(availableValues.map((value) => value.id));

    if (option.design === designs.checkbox || option.design === designs.file || option.design === designs.text) {
      const selectedIds = Array.isArray(selected)
        ? selected.filter((id) => availableIds.has(id) && (option.design !== designs.text || form.value.options_data[id]?.type === 'text' && !!form.value.options_data[id]?.value.trim()))
        : [];
      const includedIds = (option.design === designs.text ? [] : availableValues)
        .filter((value) => value.part_from_product)
        .map((value) => value.id);
      const merged = [...new Set([...selectedIds, ...includedIds])];

      if (merged.length) next[option.id] = merged;
      continue;
    }

    if (typeof selected === "number" && availableIds.has(selected)) {
      next[option.id] = selected;
      continue;
    }


    if (option.design === designs.file && !option.values.some((value) => value.part_from_product)) continue

    const preferred = getPreferredOptionValue(option);
    if (preferred) next[option.id] = preferred.id;
  }

  if (!areSelectedOptionsEqual(form.value.options, next)) {
    form.value.options = next;
  }

  const selectedValueIds = new Set(Object.values(next).flat());
  for (const valueId of Object.keys(form.value.options_data)) {
    if (!selectedValueIds.has(Number(valueId))) Reflect.deleteProperty(form.value.options_data, valueId);
  }
};

const availablePcs = computed(() => {
  if (!product.value) return 0;
  return getMaxAvailableCountPcs(product.value, form.value.options);
});

const maxAvailablePcs = computed(() =>
  Math.floor(availablePcs.value / selectedConversionAmount.value),
);
const maxAvailableWithoutConversion = computed(() => availablePcs.value);
const real_pcs_selected = computed(
  () => Number(form.value.count || 0) * selectedConversionAmount.value,
);

const quantityMin = computed(() =>
  maxAvailablePcs.value > 0 ? minOrderCount : 0,
);

const normalizeCount = (value: number | string) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || maxAvailablePcs.value <= 0) {
    return quantityMin.value;
  }

  return Math.min(maxAvailablePcs.value, Math.max(minOrderCount, numeric));
};

const syncCount = () => {
  const normalized = normalizeCount(form.value.count);
  if (form.value.count !== normalized) form.value.count = normalized;
};

const decreaseQuantity = () => {
  form.value.count = normalizeCount(Number(form.value.count || 0) - 1);
};

const increaseQuantity = () => {
  form.value.count = normalizeCount(Number(form.value.count || 0) + 1);
};

const requiredOptionsSelected = computed(() =>
  options.value.every((option) => {
    if (
      option.design !== designs.radio_required &&
      option.design !== designs.select
    ) {
      return true;
    }

    return typeof form.value.options[option.id] === "number";
  }),
);

const canPurchase = computed(
  () =>
    Boolean(product.value?.can_order) &&
    (!subscriptionMonths.value || (offersSubscriptions.value && subscriptionCheckoutAvailable.value && Boolean(userStore.user))) &&
    Boolean(selected_conversion.value) &&
    requiredOptionsSelected.value &&
    (!product.value?.is_booking || !!bookingStart.value) &&
    maxAvailablePcs.value > 0 &&
    Number(form.value.count) >= minOrderCount,
);

const price = computed(() => {
  if (!product.value) return 0;
  if (!selected_conversion.value) return 0;
  if (!product.value.count) return product.value.price;
  const options = product.value.options;
  const base = getBasePrice(
    selected_conversion.value,
    options,
    form.value.options,
  );
  const extras_pcs = getOptionExtras(
    base / selected_conversion.value.conversion,
    options,
    form.value.options,
  );
  return (
    (base + extras_pcs * selected_conversion.value.conversion) *
    form.value.count
  );
});
const discount = computed(() => selected_conversion.value?.discount?.[0] || 0);

const addItem = async (convertToOrder: boolean = false) => {
  syncCount();
  syncSelectedOptions();
  if (
    !product.value ||
    !selected_conversion.value ||
    !canPurchase.value ||
    (convertToOrder && subscriptionMonths.value !== null) ||
    (convertToOrder ? !settings.value?.enable_fast_order : !product.value.can_sell)
  ) {
    return false;
  }

  try {
    return await addToCart(
    product.value.id,
    selected_conversion.value.code,
    selected_conversion.value.conversion,
    form.value.count,
    form.value.options,
    form.value.options_data,
    false,
    convertToOrder,
    product.value.is_booking && bookingStart.value ? { starts_at: bookingStart.value } : null,
    subscriptionMonths.value
    );
  } catch (error) {
    if (product.value.is_booking) { await bookingCalendar.value?.refresh(); return false; }
    throw error;
  }
};

const modalFast = ref(false);
const openFastModal = () => {
  if (!subscriptionMonths.value && settings.value?.enable_fast_order && canPurchase.value) modalFast.value = true;
};
const closeFastModal = () => (modalFast.value = false);

const { _user } = storeToRefs(userStore);
const c_form = reactive({
  payment_service: settings.value?.fast_order_payment_service ?? "cash",
  fast_order: true,
  terms: false,
  user_data: {
    first_name: _user.value?.first_name,
    last_name: _user.value?.last_name,
    email: _user.value?.email,
    phone: _user.value?.phone,
  } as userInterface,
} as checkoutInterface);
const formRef = ref<FormInstance>();
const fast_form_rules: FormRules = {
  "user_data.first_name": [
    { required: true, message: t("first_name_required"), trigger: "blur" },
  ],
  "user_data.last_name": [
    { required: true, message: t("last_name_required"), trigger: "blur" },
  ],
  "user_data.email": [
    { required: true, message: t("email_required"), trigger: "blur" },
    { type: "email", message: t("email_invalid"), trigger: "blur" },
  ],
  "user_data.phone": [
    { required: true, whitespace: true, message: t("phone_required"), trigger: "blur" },
    { validator: phoneValidator, trigger: ["blur", "change"] },
  ],
  terms: [
    {
      type: "enum",
      enum: [true],
      required: true,
      message: t("terms_required"),
      trigger: "change",
    },
  ],
};
const placeOrderLoader = ref(false);
const createFastOrder = async () => {
  if (placeOrderLoader.value || !settings.value?.enable_fast_order || !canPurchase.value) return;

  const ok = await formRef.value?.validate().catch(() => false);
  if (!ok) return;

  placeOrderLoader.value = true;
  const { placeOrder, buildOrderRequest } = useOrder();
  try {
    const added = await addItem(true);
    if (!added) return;

    const cart = cartStore.cart;
    const order = buildOrderRequest(
      cart,
      settings.value.currency,
      cartStore.total,
      cartStore.total_no_vat,
      { ...c_form, payment_service: settings.value?.fast_order_payment_service ?? "cash" },
      { is_valid: true },
    );
    await placeOrder(cart, order);
    closeFastModal();
  } finally {
    placeOrderLoader.value = false;
  }
};

const syncMobileState = () => {
  is_mobile.value = window.innerWidth < 768;
};

watch(
  () => _user.value,
  (user) => {
    if (!user) return;

    c_form.user_data.first_name ||= user.first_name;
    c_form.user_data.last_name ||= user.last_name;
    c_form.user_data.email ||= user.email;
    c_form.user_data.phone ||= user.phone;
  },
  { immediate: true },
);

onMounted(() => {
  if (product.value) tracking.viewItem(product.value);
  syncMobileState();
  window.addEventListener("resize", syncMobileState, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", syncMobileState);
});

watch(
  () => form.value.code,
  () => {
    syncCount();
    syncSelectedOptions();
  },
);
watch(
  () => [form.value.count, maxAvailablePcs.value],
  () => {
    const previous = form.value.count;
    syncCount();
    if (previous === form.value.count) syncSelectedOptions();
  },
  { immediate: true },
);
watch(
  () => real_pcs_selected.value,
  () => syncSelectedOptions(),
);
watch(
  () => options.value,
  () => syncSelectedOptions(),
);
watch(
  () => product.value,
  async (p) => {
    if (!p?.id) return;

    currentSlide.value = 0;
    loadedSlides.value = new Set([0]);
    subscriptionMonths.value = null;
    const preferredConversion =
      p.conversions.find((c) => c.code === "PCS") || p.conversions[0];
    form.value.code = preferredConversion?.code || "PCS";
    form.value.count = minOrderCount;
    form.value.options_data = {};
    form.value.options = buildDefaultOptions(
      options.value,
      minOrderCount * selectedConversionAmount.value,
    );
    syncCount();
    syncSelectedOptions();

    await Promise.all([
      colors.value.load(p),
      related.value.load(p),
      pages.value.load(p),
    ]);
  },
  { immediate: true, deep: false },
);

const attributes = computed(() =>
  Object.entries(product.value?.attributes || []),
);

const drawer=ref(false)
const pageInfo=ref({} as PageInterface)


const openPage=async (url:string)=>{
  const { $api } = useNuxtApp();
  const response=await $api<PageInterface>(url, { method: 'GET' })
  drawer.value=true
  pageInfo.value=response

}

const addDataTo = (optionId: number, valueId: number, type: string, value: string): void => {
    if (!Array.isArray(form.value.options[optionId])) form.value.options[optionId] = [];

    if (value.trim()) {
        form.value.options_data[valueId] = { type, value };
        if (!form.value.options[optionId].includes(valueId)) {
            form.value.options[optionId].push(valueId);
        }
        return;
    }

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete form.value.options_data[valueId];
    form.value.options[optionId] = form.value.options[optionId].filter(id => id !== valueId);
};

const optionFileUrl = (valueId: number) => form.value.options_data[valueId]?.value || ''
const optionTextValue = (valueId: number) => form.value.options_data[valueId]?.type === 'text' ? form.value.options_data[valueId]?.value || '' : ''

const uploadFile = async (
    productOptionId: number,
    productOptionValueId: number,
) => {
    const fileUrl = await openDialog<string>(
        UploadFileToOptionComponent,
        {
            title: t('product_option_file_upload'),
            width: '600px',
            props: {
                productId:product.value!.id,
                productOptionId,
                productOptionValueId,
            },
        },
    )

    if (!fileUrl) {
        return
    }

    addDataTo(productOptionId, productOptionValueId, 'file', fileUrl);
}

const seoInfo = computed(() => ({
  name: product.value?.name || "",
  short_description: product.value?.short_description || null,
  photo: product.value?.photo || product.value?.thumb || null,
  icon: product.value?.icon || null,
  schemaType: "product" as const,
  sku: product.value?.code,
  brand: product.value?.brand,
  price: price.value,
  currency: settings.value.currency,
  discount: discount.value,
  count: maxAvailableWithoutConversion.value,
  code: product.value?.code,
  rating: product.value?.rating,
  rating_count: product.value?.rating_count,
}));

usePageSeo(seoInfo);
</script>

<template>
  <div class="product-page-root">
  <section class="container-xxl product-page">
    <el-breadcrumb separator="/" class="pt-0 pt-lg-1 mb-3">
      <el-breadcrumb-item :to="{ path: '/' }">
        {{ $t("home_page") }}
      </el-breadcrumb-item>
      <el-breadcrumb-item
        v-for="cat in product?.categories_list"
        :key="cat.url"
        :to="{ path: cat.url }"
        >{{ cat.name }}</el-breadcrumb-item
      >
      <el-breadcrumb-item>{{ product?.name }}</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="row">
      <div class="col-lg-8 pe-lg-4">
        <div class="row">
          <div class="col-md-8 mb-3">
            <article class="d-flex flex-md-column flex-column-reverse">
              <h1 class="single-product-name mb-1 mb-lg-4">
                {{ product?.name }}
              </h1>
              <div class="product-gallery mb-4 mt-2 mt-lg-0 mb-lg-2">

                <client-only>
                  <Carousel
                    id="gallery"
                    v-bind="galleryConfig"
                    v-model="currentSlide"
                  >
                    <Slide
                      v-for="(image, index) in images"
                      :key="image.photo || image.thumb || image.icon"
                    >
                      <el-image
                        v-if="loadedSlides.has(index)"
                          preview-teleported
                        :src="image.photo || image.thumb"
                        :preview-src-list="images.map(i => i.photo || i.thumb)"
                        :initial-index="index"
                        width="1000"
                        :fit="settings?.img_product_page_fill || 'cover'"
                        height="750"
                        :alt="
                          product?.name
                            ? `${product.name} - image ${index + 1}`
                            : 'Product image'
                        "
                        class="gallery-image"
                        :loading="index === 0 ? 'eager' : 'lazy'"
                        :fetchpriority="index === 0 ? 'high' : 'auto'"
                        decoding="async"
                      />
                    </Slide>
                  </Carousel>
                  <Carousel
                    v-if="images.length > 1"
                    id="thumbnails"
                    v-bind="thumbnailsConfig"
                    v-model="currentSlide"
                  >
                    <Slide v-for="image in images" :key="image.icon">
                      <template #default="{ currentIndex, isActive }">
                        <button
                          type="button"
                          :class="['thumbnail', { 'is-active': isActive }]"
                          :aria-label="
                            product?.name
                              ? `${product.name} - thumbnail ${
                                  currentIndex + 1
                                }`
                              : 'Thumbnail'
                          "
                          @click="slideTo(currentIndex)"
                        >
                          <img
                            :src="image.icon || image.thumb"
                            :alt="
                              product?.name
                                ? `${product.name} - thumbnail`
                                : 'Thumbnail'
                            "
                            class="thumbnail-image"
                            loading="lazy"
                            decoding="async"
                            width="80"
                            height="60"
                          />
                        </button>
                      </template>
                    </Slide>

                    <template #addons>
                      <Navigation />
                    </template>
                  </Carousel>
                  <template #fallback>
                    <div>
                      <img
                        :src="galleryFallbackImage"
                        :alt="
                          product?.name
                            ? `${product.name} - image`
                            : 'Product image'
                        "
                        width="1000"
                        height="750"
                        class="gallery-image"
                        fetchpriority="high"
                      >
                      <div v-if="images.length > 1" class="gallery-thumbnails-placeholder" aria-hidden="true" />
                    </div>
                  </template>
                  <div class="fav-btn-absolute">
                    <el-button
                      v-if="product?.id && !in_favorites(product.id)"
                      aria-label="Add to favorites"
                      :aria-pressed="
                        in_favorites(product?.id) ? 'true' : 'false'
                      "
                      text
                      circle
                      class="btn fav-toggle"
                      :loading="favLoading?.[product.id]"
                      @click="addToFavorites(product)"
                    >
                      <Icon name="lets-icons:favorite-light" size="22" />
                    </el-button>
                    <el-button
                      v-else-if="product?.id"
                      aria-label="Remove from favorites"
                      :aria-pressed="
                        in_favorites(product.id) ? 'true' : 'false'
                      "
                      circle
                      text
                      class="btn fav-toggle"
                      type="primary"
                      :loading="favLoading?.[product.id]"
                      @click="removeFromFavorites(product)"
                    >
                      <Icon name="lets-icons:favorite-fill" size="22" />
                    </el-button>
                  </div>
                </client-only>
              </div>
            </article>
          </div>
          <div class="col-md-4 mb-3">
            <div id="mobile_price"></div>
            <rate-component v-if="product" :key="product.id" :product="product" />
            <div class="productInfoBlock" v-if="colors.list.length">
              <el-divider
                class="my-3"
                border-style="solid"
                :style="{ borderColor: 'var(--el-bg-color-page)' }"
              />
              <div class="title">
                <icon name="material-symbols-light:colorize-sharp"></icon>
                {{ $t("colors") }}
              </div>

              <div class="description colors-list">
                <div class="row">
                  <div
                    class="col-md-4 col-4 mb-3"
                    v-for="color in colors.list"
                    :key="color.id"
                  >
                    <NuxtLinkLocale :to="color.url">
                      <img
                        class="attr-img"
                        :src="color.icon"
                        :alt="color.name"
                        loading="lazy"
                      >
                    </NuxtLinkLocale>
                  </div>
                </div>
              </div>

            </div>
            <el-skeleton v-if="colors.loading" animated :rows="2"></el-skeleton>
            <el-divider
              class="my-3"
              border-style="solid"
              :style="{ borderColor: 'var(--el-bg-color-page)' }"
            />
            <div class="productInfoBlock">
              <div class="title">
                <Icon name="material-symbols-light:chat-info-outline-rounded" />
                {{ $t("information") }}
              </div>
              <ul class="description">
                <li v-for="page of pages.list"
                    :key="page.url"
                    class="mb-1 page-link">
                    <span class="page-link-icon">
                      <Icon name="material-symbols-light:arrow-forward-ios" size="16" />
                    </span>

                  <el-button link type="primary" @click="openPage(page.url)">
                    {{ page.name }}
                  </el-button>
                </li>

                <li class="mb-1 page-link">
                    <span class="page-link-icon">
                      <Icon name="material-symbols-light:arrow-forward-ios" size="16" />
                    </span>
                  <el-button link type="info" @click="openPage('/page/return')">
                    {{ $t("return_information") }}
                  </el-button>
                </li>

                <li class="mb-1 page-link">
                    <span class="page-link-icon">
                      <Icon name="material-symbols-light:arrow-forward-ios" size="16" />
                    </span>
                  <el-button link type="info" @click="openPage('/page/delivery')">
                    {{ $t("delivery_information") }}
                  </el-button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-if="attributes.length" class="productInfoBlock mb-4">
          <div class="title">
            <Icon name="material-symbols-light:data-info-alert" />
            {{ $t("characteristics") }}
          </div>
          <div class="description">
            <el-descriptions
              :aria-label="`${product?.name || 'Product'} characteristics`"
              class="mt-3 product-attrs-vars"
              border
              :column="is_mobile ? 1 : 2"
            >
              <el-descriptions-item
                v-for="[attr, val] in attributes"
                :key="attr"
                :label="attr"
              >
                {{ val.join(", ") }}
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </div>
        <div id="rate_list"></div>
        <div class="productInfoBlock">
          <div class="title">
            <Icon name="material-symbols-light:info-outline" />
            {{ $t("description") }}
          </div>
          <div
            class="description"
            role="region"
            :aria-label="`${product?.name || 'Product'} description`"
          >
            <div
              class="product-description"
              v-html="product?.description || ''"
            ></div>
          </div>
        </div>
      </div>
      <aside
        class="col-lg-4 sticky"
        role="complementary"
        aria-label="Purchase and related products"
      >
        <div id="web_price"></div>
        <h2 class="section_title mb-4 mt-5">{{ $t("related_products") }}</h2>
        <div
          class="related-block"
          v-loading="related.loading"
          role="region"
          :aria-label="`Related products for ${product?.name || ''}`"
        >
          <ProductListSimple :list="related.list" v-if="related.list.length" />
        </div>
      </aside>
    </div>
  </section>

  <ClientOnly>
    <teleport
      v-if="product?.id"
      :key="is_mobile ? 'm' : 'd'"
      :to="is_mobile ? '#mobile_price' : '#web_price'"
    >
      <!-- Price and Add to cart -->
      <div v-if="product?.id" class="mb-4 single-price-box">
        <div class="mb-2">
          <el-space spacer="|" wrap>
            <el-text class="single-box-sku" type="info">
              {{ $t("sku") }}: {{ product?.code || product?.id }}
            </el-text>

            <el-text class="single-box-brand" v-if="product?.brand" type="info">{{product?.brand || "" }}</el-text>
          </el-space>
        </div>
        <div class="productInfoBlock">
          <el-space spacer="|" wrap>
          <div class="title mb-0">
            <icon name="lets-icons:money-light"/>
            {{ $t("price") }}
          </div>
            <el-text v-if="product.is_preorder" type="primary">{{ $t('preorder_label') }}</el-text>
            <el-text
                v-else-if="maxAvailablePcs"
                type="primary"
                class="in-stock-label"
                style=""
            >{{ $t("in_stock") }} </el-text
            >
            <el-text v-else type="info">{{ $t("not_in_stock") }}</el-text>
          </el-space>
          <div class="price_row mb-3 mt-2">
            <div class="d-flex justify-content-start ">
              <div class="price">{{ format(price) }}</div>
              <div v-if="discount" class="old_price ms-2">
                {{ format(oldPrice(price, discount)) }}
              </div>
              <el-tag
                v-if="discount"
                round
                class="fw-bold discount-badge ms-3"
                effect="dark"
                >{{ discount * -1 }} %</el-tag
              >
            </div>
          </div>
          <p v-if="subscriptionMonths" class="small">{{ $t('subscription_catalog_monthly') }}</p>
        </div>
          <template v-if="product.can_sell || (settings?.enable_fast_order && product.can_order)">

          <ProductSubscriptionTerms
            v-if="offersSubscriptions && product.subscription"
            v-model="subscriptionMonths"
            :subscription="product.subscription"
            :checkout-available="subscriptionCheckoutAvailable"
            :shipping-mode="settings?.subscriptions?.shipping_mode"
            :monthly-base-price="price"
            :quantity="Number(form.count)"
          />
          <ProductPreorderTerms
            v-if="product.is_preorder"
            :percent="product.preorder_deposit_percent"
            :days="product.preorder_lead_days"
            class="mb-3"
          />
          <!----- OPTIONS ----->
        <template v-if="options.length">
          <template v-for="option in options" :key="option.id">
            <div class="productInfoBlock">
              <div class="title">

                <icon name="material-symbols-light:tune" />

                {{ option.name }}
              </div>
              <div class="description">
                <el-radio-group
                  v-if="option.design === designs.radio_required"
                  v-model="form.options[option.id] as number"
                  :aria-label="option.name"
                  :size="option.values.length > 5 ? 'small' : 'default'"
                  class="conversion-buttons options-buttons"
                >
                  <el-radio-button
                    v-for="value in option.values"
                    :key="value.id"
                    :value="value.id"
                    :class="{ 'options-buttons-img': value.icon }"
                    :disabled="!isOptionValueAvailable(value)"
                  >
                    <span v-if="!value.icon">{{ value.name }}</span>
                    <img
                      v-else
                      :src="value.icon"
                      :alt="value.name"
                      :title="value.name"
                      loading="eager"
                    >
                  </el-radio-button>
                </el-radio-group>
                <el-checkbox-group
                  v-if="option.design === designs.checkbox"
                  v-model="form.options[option.id] as number[]"
                  :aria-label="option.name"
                >
                  <el-checkbox
                    v-for="value in option.values"
                    :key="value.id"
                    :value="value.id"
                    :disabled="!isOptionValueAvailable(value)"
                    >{{ value.name }}</el-checkbox
                  >
                </el-checkbox-group>
                  <el-select
                      v-if="option.design === designs.select"
                      v-model="form.options[option.id]"
                      :aria-label="option.name"
                      :clearable="false"
                      :placeholder="$t('please_select_option')"
                      :options="availableOptionValues(option)"
                      :disabled="!availableOptionValues(option).length"
                      :no-data-text="$t('no_available_options')"
                      :props="{ value: 'id', label: 'name' }"
                  />
                  <template v-if="option.design === designs.file">
                    <div class="file-option-list">
                      <div
                        v-for="ov in availableOptionValues(option)"
                        :key="ov.id"
                        class="file-option-item"
                      >
                        <el-text class="file-option-name">{{ ov.name }}</el-text>
                        <div class="file-option-actions">
                          <el-button
                            type="primary"
                            plain
                            round
                            @click="uploadFile(option.id, ov.id)"
                          >
                            <Icon name="lucide:upload" size="16" />
                            {{
                              optionFileUrl(ov.id)
                                ? $t('product_option_file_change')
                                : $t('product_option_file_upload')
                            }}
                          </el-button>
                          <el-link
                            v-if="optionFileUrl(ov.id)"
                            :href="optionFileUrl(ov.id)"
                            target="_blank"
                            rel="noopener"
                            type="success"
                            :underline="false"
                          >
                            {{ $t('product_option_file_preview_uploaded') }}
                          </el-link>
                        </div>
                      </div>
                    </div>
                  </template>
                  <div v-if="option.design === designs.text" class="file-option-list">
                    <div
                      v-for="ov in availableOptionValues(option)"
                      :key="ov.id"
                      class="file-option-item"
                    >
                      <el-text class="file-option-name">{{ ov.name }}</el-text>
                      <el-input
                        class="text-option-input"
                        :aria-label="`${option.name}: ${ov.name}`"
                        :model-value="optionTextValue(ov.id)"
                        :placeholder="$t('product_option_text_placeholder')"
                        :maxlength="200"
                        show-word-limit
                        clearable
                        @input="(value: string) => addDataTo(option.id, ov.id, 'text', value)"
                      />
                    </div>
                  </div>
              </div>
            </div>
            <el-divider
              class="my-3"
              border-style="solid"
              :style="{ borderColor: 'var(--el-color-primary-light-8)' }"
            />
          </template>
        </template>


        <!----- CONVERSIONS ----->
        <div
          v-if="!product.is_booking && product?.conversions?.length && product.conversions.length > 1"
          class="productInfoBlock"
        >
          <div class="title">
            <icon name="material-symbols-light:package-2-outline"></icon>
            {{ $t("conversion") }}
          </div>
          <div class="description">
            <el-radio-group v-model="form.code" class="conversion-buttons">
              <el-radio-button
                v-for="value in product?.conversions"
                :key="value.code"
                :value="value.code"
                :disabled="maxAvailableWithoutConversion < value.conversion"
                >{{ $t(value.code) }}
                <span v-if="value.conversion > 1">
                  ({{ value.conversion }}
                  {{ $t("short_" + product.unit) }})</span
                ></el-radio-button
              >
            </el-radio-group>
          </div>
        </div>
        <el-divider
          class="my-3"
          border-style="solid"
          v-if="!product.is_booking && product?.conversions?.length && product.conversions.length > 1"
          :style="{ borderColor: 'var(--el-color-primary-light-8)' }"
        />

        <BookingCalendar v-if="product.is_booking" ref="bookingCalendar" v-model="bookingStart" :product-id="product.id" />
        <!----- QUANTITY ----->
        <div v-if="!product.is_booking" class="productInfoBlock">
          <div class="title">
            <icon name="material-symbols-light:hand-package-outline"></icon>
            {{ $t(subscriptionMonths ? 'subscription_quantity_label' : 'quantity') }}
          </div>
          <div class="description d-flex justify-content-start">
            <div class="qty-row">
              <el-button
                circle
                type="primary"
                text
                class="qty-button"
                :disabled="form.count <= quantityMin"
                aria-label="Minus"
                @click="decreaseQuantity"
              >
                <Icon name="lets-icons:remove-fill" />
              </el-button>
              <input
                class="qty-value"
                v-model.number="form.count"
                type="number"
                inputmode="numeric"
                :min="quantityMin"
                :max="maxAvailablePcs"
                :disabled="!maxAvailablePcs"
                aria-label="Quantity"
                @blur="syncCount"
              />
              <el-button
                text
                type="primary"
                circle
                class="qty-button"
                :disabled="form.count >= maxAvailablePcs"
                aria-label="Plus"
                @click="increaseQuantity"
              >
                <Icon name="lets-icons:add-ring-fill" />
              </el-button>
            </div>
          </div>
        </div>
        <el-divider
          class="my-3"
          border-style="solid"
          :style="{ borderColor: 'var(--el-color-primary-light-8)' }"
        />

        <NuxtLinkLocale v-if="product.can_sell && subscriptionMonths && !userStore.user" to="/login" class="cart-btn custom-button mb-3">
          <span class="icon"><Icon name="lucide:log-in" :size="24" /></span>
          <span class="label">{{ $t('subscription_sign_in') }}</span>
        </NuxtLinkLocale>
        <button
          v-else-if="product.can_sell"
          v-loading="adding[product.id]"
          :disabled="adding[product.id] || !canPurchase"
          class="cart-btn custom-button mb-3"
          @click="addItem(false)"
        >
          <span class="icon"
            ><Icon name="lucide:shopping-cart" :size="30"
          /></span>
          <span class="label">{{ $t(product.is_preorder ? "preorder_cta" : subscriptionMonths ? 'subscription_add_to_cart' : "add_to_cart") }}</span>
        </button>

        </template>
          <template v-if="settings?.enable_phone_order">
          <a
            :href="settings?.phone ? `tel:${settings.phone}` : undefined"
            :class="[
              'call-btn custom-button mb-3',
              { 'is-disabled': !settings?.phone },
            ]"
            rel="nofollow"
            :aria-disabled="!settings?.phone ? 'true' : 'false'"
            :tabindex="!settings?.phone ? -1 : 0"
            :title="$t('call_order')"
            :aria-label="
              settings?.phone
                ? `${$t('call_order')} — ${settings.phone}`
                : $t('call_order')
            "
            @click="tracking.callTo(product);"
          >
            <span class="icon"
              ><Icon name="lets-icons:phone" :size="30"
            /></span>
            <span class="label">{{ $t("call_order") }}</span>
          </a>
        </template>
          <template v-if="!subscriptionMonths && product.can_order && settings?.enable_fast_order">
          <button
            class="fast-btn custom-button mb-3"
            :disabled="!canPurchase"
            @click="openFastModal"
          >
            <span class="icon"><Icon name="lets-icons:bag" :size="30" /></span>
            <span class="label">{{ $t("fast_order") }}</span>
          </button>
        </template>
      </div>
    </teleport>
  </ClientOnly>
  <el-dialog
    v-model="modalFast"
    :title="$t('fast_order')"
    width="min(520px, 92vw)"
    :close-on-click-modal="!placeOrderLoader"
    @close="closeFastModal"
  >
    <el-divider
      class="mb-3 mt-0"
      border-style="solid"
      :style="{ borderColor: 'var(--el-bg-color-page)' }"
    />
    <ProductPreorderTerms
      v-if="product?.is_preorder"
      :percent="product.preorder_deposit_percent"
      :days="product.preorder_lead_days"
      class="mb-3"
    />
    <el-form
      ref="formRef"
      :model="c_form"
      :rules="fast_form_rules"
      @submit.prevent="createFastOrder"
      label-position="top"
    >
      <div class="row">
        <el-form-item
          class="col-12 col-sm-6"
          :label="$t('first_name')"
          prop="user_data.first_name"
        >
          <el-input
            v-model="c_form.user_data.first_name"
            class="custom_inputs"
            autocomplete="given-name"
          ></el-input>
        </el-form-item>

        <el-form-item
          class="col-12 col-sm-6"
          :label="$t('last_name')"
          prop="user_data.last_name"
        >
          <el-input
            v-model="c_form.user_data.last_name"
            class="custom_inputs"
            autocomplete="family-name"
          ></el-input>
        </el-form-item>

        <el-form-item class="col-12 col-sm-6" :label="$t('phone')" prop="user_data.phone">
          <el-input
            v-model="c_form.user_data.phone"
            class="custom_inputs"
            autocomplete="tel"
          ></el-input
        ></el-form-item>
        <el-form-item class="col-12 col-sm-6" :label="$t('email')" prop="user_data.email">
          <el-input
            v-model="c_form.user_data.email"
            class="custom_inputs"
            autocomplete="email"
          ></el-input>
        </el-form-item>
      </div>
      <el-form-item prop="terms">
        <el-checkbox v-model="c_form.terms">
          {{ $t("accept_checkout_terms") }}
          <NuxtLinkLocale to="/page/terms">{{
            $t("accept_checkout_terms2")
          }}</NuxtLinkLocale>
        </el-checkbox>
      </el-form-item>

      <el-button
        native-type="submit"
        type="primary"
        round
        :loading="placeOrderLoader"
        >{{ $t("order_now") }}</el-button
      >
      <el-button round @click="closeFastModal">
        {{ $t("cancel") }}
      </el-button>
    </el-form>
  </el-dialog>
  </div>
  <el-drawer
      :size="is_mobile ? '100%' : '40%'"
      v-model="drawer"
      class="el-drawer-page"
  >
    <template #header>
      <h3 class="drawer-title">{{ pageInfo.name }}</h3>
    </template>

    <div class="drawer-content" v-html="pageInfo.description"></div>
  </el-drawer>
</template>

<style scoped lang="scss">
.drawer-content p img{
  width: 100%;
}
.drawer-title {
  font-size: 1.4rem;
  margin: 0;
  @media (max-width: 992px) {
    font-size: 1.2rem;
  }
}
.fav-btn-absolute {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
}
.product-attrs-vars {
  --el-descriptions-item-bordered-label-background: #fff;
}
.product-page {
  .el-breadcrumb {
    font-size: var(--fs--1);
    line-height: 1.25;
    @media (max-width: 992px) {
      font-size: var(--fs--2);
    }
  }

  .single-product-name {
    font-size: var(--fs-3);
    line-height: calc(var(--fs-3) + 3px);
    @media (max-width: 992px) {
      font-size: var(--fs-2);
      text-align: center;
      line-height: calc(var(--fs-2) + 3px);
    }
  }
  .attr-img {
    width: 65px;
  }
  .colors-list {
    max-height: 300px;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 4px;
  }
  .single-price-box {
    border-radius: var(--el-border-radius-round);
    padding: 15px;
    margin-bottom: 10px;
    background: color-mix(in srgb, var(--el-color-primary) 5%, white);
    border: 1px solid color-mix(in srgb, var(--el-color-primary) 10%, white);
  }

  .product-gallery {
    position: relative;


    .fav-toggle {
      background: color-mix(in srgb, white 88%, transparent);
      box-shadow: 0 4px 14px rgb(0 0 0 / 10%);
      backdrop-filter: blur(8px);
    }

    .gallery-image {
      display: block;
      width: 100%;
      height: auto;
      aspect-ratio: 4 / 3;
      border-radius: 8px;
      object-fit: var(--img-product-page-fill);
    }

    .gallery-thumbnails-placeholder {
      height: 80px;
      margin-top: 12px;
    }

    #thumbnails {
      margin-top: 12px;
      .thumbnail {
        appearance: none;
        background: transparent;
        border: 0;
        cursor: pointer;
        padding: 2px;
        border-radius: 4px;
        width: 100%;
        transition:
          transform 0.15s ease,
          box-shadow 0.15s ease;
        .thumbnail-image {
          width: 100%;
          aspect-ratio: 4 / 3;
          border-radius: 4px;
          object-fit: contain;
        }
        &.is-active {
          transform: scale(1.02);
          box-shadow: 0 0 0 2px var(--el-color-primary);
        }
      }
    }
  }

  .sticky {
    position: sticky;
    top: 9rem;
    align-self: flex-start;
  }

  .in-stock-label {
    color: var(--el-color-primary-dark-1);
  }
  .price_row {
    .price {
      font-size: var(--fs-4);
      color: var(--el-color-primary-dark-1);
    }
    .discount {
      display: flex;
      align-items: center;
      gap: 12px;
      justify-content: end;
    }
    .percent {
      background: var(--el-color-primary);
      color: white;
      padding: 2px 8px;
      border-radius: 8px;
    }
    .old_price {
      font-size: var(--fs-2);
      text-decoration: line-through;
      color: var(--el-color-info);
    }
  }

  .conversion-buttons {
    --el-border: 2px solid var(--el-color-primary);
    min-width: 130px;
    :deep(.el-radio-button__inner) {
      border-radius: var(--el-border-radius-round);
      margin-right: 10px;
    }
    &.options-buttons {
      .el-radio-button {
        --el-border: 2px solid var(--el-color-primary-light-8);
        &.is-active {
          --el-border: 2px solid var(--el-color-primary);
          --el-radio-button-checked-bg-color: none !important;
          --el-radio-button-checked-text-color: var(
            --el-text-color-primary
          ) !important;
        }
      }
    }
    .options-buttons-img:deep(.el-radio-button__inner) {
      padding: 0;
      border-radius: 0;

      img {
        width: 25px;
        height: 25px;
        object-fit: contain;
        display: block;
      }
    }
  }

  .file-option-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .file-option-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 10px 0;
    border-bottom: 1px solid var(--el-border-color-lighter);

    &:last-child {
      border-bottom: 0;
    }
  }

  .file-option-name {
    font-weight: 600;
  }

  .file-option-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .text-option-input {
    width: min(100%, 420px);
  }

  .fav-btn {
    --background: none;
    --background-hover: linear-gradient(
      to right,
      white,
      var(--el-color-primary-light-8)
    );
    --background-icon: none;
    --border: var(--nav-bg);
    --color: var(--nav-bg);
    --color-icon: var(--nav-bg);
  }

  .cart-btn {
    --background: linear-gradient(
      to right,
      var(--el-color-primary-dark-1),
      var(--el-color-primary)
    );
    --background-hover: linear-gradient(
      to right,
      var(--el-color-primary-dark-2),
      var(--el-color-primary-dark-1)
    );
    --background-icon: var(--el-color-primary-dark-2);
    --border: none;
    --color: var(--nav-text);
    --color-hover: var(--nav-text);
    --color-icon: var(--nav-text);
  }
  .call-btn {
    --background: white;
    --background-hover: linear-gradient(
        to right,
        var(--el-color-primary-dark-2),
        var(--el-color-primary-dark-1)
    );
    --background-icon: white;
    --border: var(--body-color);
    --color: var(--body-color);
    --color-hover: var(--nav-text);
    --color-icon: var(--body-color);
  }
  .call-btn.is-disabled,
  .fast-btn:disabled,
  .cart-btn:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .fast-btn {
    --background: white;
    --background-hover: linear-gradient(
      to right,
      var(--el-color-primary-dark-2),
      var(--el-color-primary-dark-1)
    );
    --background-icon: white;
    --border: var(--body-color);
    --color: var(--body-color);
    --color-hover: var(--nav-text);
    --color-icon: var(--body-color);
  }
  .qty-value {
    border-bottom-color: var(--el-color-primary);
    &:disabled {
      color: var(--el-text-color-secondary);
      background: transparent;
    }
  }
}
.page-link {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.page-link-icon {
  flex-shrink: 0;
  line-height: 1.4;
}

.page-link :deep(.el-button) {
  min-width: 0;
  height: auto;
  margin: 0;
  padding: 0;
  white-space: normal;
  text-align: left;
  line-height: 1.4;
}

.page-link :deep(.el-button > span) {
  display: block;
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
